import { useState } from 'react';
import { useAuth } from '@/hooks/useAuth';

interface Props {
  onBack: () => void;
}

type Step = 'login' | 'register';

export default function DriverLogin({ onBack }: Props) {
  const { signIn, signUp, resetPassword } = useAuth();
  const [step, setStep] = useState<Step>('login');

  // Login
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Register
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirm, setRegConfirm] = useState('');
  const [showRegPwd, setShowRegPwd] = useState(false);
  const [regError, setRegError] = useState('');
  const [regLoading, setRegLoading] = useState(false);
  const [regSuccess, setRegSuccess] = useState(false);

  // Forgot password
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotMsg, setForgotMsg] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    const { error } = await signIn(email, password);
    if (error) setLoginError(error);
    setLoginLoading(false);
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegError('');
    if (!regName.trim()) { setRegError('Le nom complet est obligatoire.'); return; }
    if (regPassword.length < 8) { setRegError('Le mot de passe doit contenir au moins 8 caractères.'); return; }
    if (regPassword !== regConfirm) { setRegError('Les mots de passe ne correspondent pas.'); return; }
    setRegLoading(true);
    const { error } = await signUp(regEmail, regPassword, regName, 'driver');
    if (error) { setRegError(error); setRegLoading(false); return; }
    setRegSuccess(true);
    setRegLoading(false);
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotMsg('');
    const { error } = await resetPassword(forgotEmail);
    setForgotMsg(error ?? 'Email envoyé ! Vérifiez votre boîte mail.');
    setForgotLoading(false);
  };

  const strengthScore = (pwd: string) => {
    let s = 0;
    if (pwd.length >= 8) s++;
    if (pwd.length >= 10) s++;
    if (/[A-Z]/.test(pwd)) s++;
    if (/[0-9]/.test(pwd)) s++;
    if (/[^A-Za-z0-9]/.test(pwd)) s++;
    return s;
  };
  const score = strengthScore(regPassword);

  const bgStyle = { background: 'linear-gradient(160deg, #0f172a 0%, #1e3a8a 50%, #1d4ed8 100%)' };

  // ── FORGOT PASSWORD OVERLAY ──
  if (showForgot) {
    return (
      <div className="fixed inset-0 z-[997] flex flex-col overflow-hidden" style={bgStyle}>
        <div className="pt-12 px-5 pb-2">
          <button onClick={() => { setShowForgot(false); setForgotMsg(''); setForgotEmail(''); }} className="flex items-center gap-2 text-blue-300 text-sm font-medium">
            <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-xl">
              <i className="ri-arrow-left-line text-white text-sm"></i>
            </div>
            <span>Retour</span>
          </button>
        </div>
        <div className="px-6 pt-4 pb-6 text-center">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-xl" style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}>
            <i className="ri-mail-send-line text-white text-2xl"></i>
          </div>
          <h1 className="text-white font-extrabold text-xl mb-1">Mot de passe oublié</h1>
          <p className="text-blue-300 text-sm">Entrez votre email pour recevoir un lien de réinitialisation</p>
        </div>
        <div className="flex-1 px-5">
          <div className="rounded-3xl p-5" style={{ background: 'rgba(255,255,255,0.07)', border: '1.5px solid rgba(255,255,255,0.12)' }}>
            <form onSubmit={handleForgot} className="space-y-4">
              <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 ${forgotEmail ? 'border-2 border-blue-400/60 bg-blue-500/10' : 'border border-white/15 bg-white/5'}`}>
                <i className="ri-mail-line text-white/40 text-sm"></i>
                <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)} placeholder="votre@email.com" className="flex-1 text-sm text-white outline-none bg-transparent placeholder-white/25" />
              </div>
              {forgotMsg && (
                <div className={`rounded-xl p-3 text-xs font-medium ${forgotMsg.includes('envoyé') ? 'bg-emerald-500/15 border border-emerald-500/25 text-emerald-300' : 'bg-red-500/15 border border-red-500/25 text-red-300'}`}>
                  {forgotMsg}
                </div>
              )}
              <button type="submit" disabled={!forgotEmail || forgotLoading} className="w-full py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-40" style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}>
                <i className={`${forgotLoading ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} text-white text-sm`}></i>
                <span className="text-white">{forgotLoading ? 'Envoi...' : 'Envoyer le lien'}</span>
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // ── REGISTER ──
  if (step === 'register') {
    return (
      <div className="fixed inset-0 z-[997] flex flex-col overflow-y-auto" style={{ background: 'linear-gradient(160deg, #0f172a 0%, #064e3b 50%, #065f46 100%)' }}>
        <div className="pt-12 px-5 pb-2 flex-shrink-0">
          <button onClick={() => { setStep('login'); setRegError(''); }} className="flex items-center gap-2 text-emerald-300 text-sm font-medium">
            <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-xl">
              <i className="ri-arrow-left-line text-white text-sm"></i>
            </div>
            <span>Retour</span>
          </button>
        </div>
        <div className="px-6 pt-3 pb-4 text-center flex-shrink-0">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-xl" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
            <i className="ri-user-add-line text-white text-2xl"></i>
          </div>
          <h1 className="text-white font-extrabold text-xl mb-1">Créer un compte</h1>
          <p className="text-emerald-300 text-sm">Rejoignez AutoLink en tant que conducteur</p>
        </div>
        <div className="flex-1 px-5 pb-10">
          {regSuccess ? (
            <div className="rounded-3xl p-6 text-center" style={{ background: 'rgba(255,255,255,0.07)', border: '1.5px solid rgba(255,255,255,0.12)' }}>
              <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-emerald-400 text-3xl"></i>
              </div>
              <h2 className="text-white font-bold text-lg mb-2">Compte créé !</h2>
              <p className="text-emerald-300 text-sm">Bienvenue sur AutoLink. Connexion en cours...</p>
            </div>
          ) : (
            <div className="rounded-3xl p-5" style={{ background: 'rgba(255,255,255,0.07)', border: '1.5px solid rgba(255,255,255,0.12)' }}>
              <form onSubmit={handleRegister} className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-emerald-300 uppercase tracking-wide mb-1.5">Nom complet *</label>
                  <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 ${regName ? 'border-2 border-emerald-400/60 bg-emerald-500/10' : 'border border-white/15 bg-white/5'}`}>
                    <i className="ri-user-line text-white/40 text-sm"></i>
                    <input type="text" value={regName} onChange={e => setRegName(e.target.value)} placeholder="Jean Dupont" className="flex-1 text-sm text-white outline-none bg-transparent placeholder-white/25" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-emerald-300 uppercase tracking-wide mb-1.5">Email *</label>
                  <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 ${regEmail ? 'border-2 border-emerald-400/60 bg-emerald-500/10' : 'border border-white/15 bg-white/5'}`}>
                    <i className="ri-mail-line text-white/40 text-sm"></i>
                    <input type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} placeholder="jean@company.be" className="flex-1 text-sm text-white outline-none bg-transparent placeholder-white/25" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-emerald-300 uppercase tracking-wide mb-1.5">Mot de passe *</label>
                  <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 ${regPassword ? 'border-2 border-emerald-400/60 bg-emerald-500/10' : 'border border-white/15 bg-white/5'}`}>
                    <i className="ri-lock-line text-white/40 text-sm"></i>
                    <input type={showRegPwd ? 'text' : 'password'} value={regPassword} onChange={e => setRegPassword(e.target.value)} placeholder="Min. 8 caractères..." className="flex-1 text-sm text-white outline-none bg-transparent placeholder-white/25" />
                    <button type="button" onClick={() => setShowRegPwd(v => !v)} className="w-5 h-5 flex items-center justify-center">
                      <i className={`${showRegPwd ? 'ri-eye-off-line' : 'ri-eye-line'} text-white/30 text-base`}></i>
                    </button>
                  </div>
                  {regPassword && (
                    <div className="flex gap-1 mt-1.5">
                      {[1,2,3,4,5].map(i => (
                        <div key={i} className={`flex-1 h-1 rounded-full ${i <= score ? (score >= 4 ? 'bg-emerald-400' : score >= 3 ? 'bg-amber-400' : 'bg-red-400') : 'bg-white/15'}`} />
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-emerald-300 uppercase tracking-wide mb-1.5">Confirmer *</label>
                  <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 ${regConfirm ? 'border-2 border-emerald-400/60 bg-emerald-500/10' : 'border border-white/15 bg-white/5'}`}>
                    <i className="ri-lock-2-line text-white/40 text-sm"></i>
                    <input type="password" value={regConfirm} onChange={e => setRegConfirm(e.target.value)} placeholder="Répéter le mot de passe..." className="flex-1 text-sm text-white outline-none bg-transparent placeholder-white/25" />
                  </div>
                </div>
                {regError && (
                  <div className="flex items-start gap-2 bg-red-500/15 border border-red-500/25 rounded-xl p-3">
                    <i className="ri-error-warning-line text-red-400 text-sm flex-shrink-0 mt-0.5"></i>
                    <p className="text-xs text-red-300 font-medium leading-snug">{regError}</p>
                  </div>
                )}
                <button type="submit" disabled={!regName.trim() || !regEmail.trim() || !regPassword || !regConfirm || regLoading} className="w-full py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 mt-2 disabled:opacity-40" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                  <i className={`${regLoading ? 'ri-loader-4-line animate-spin' : 'ri-user-add-line'} text-white text-sm`}></i>
                  <span className="text-white">{regLoading ? 'Création...' : 'Créer mon compte'}</span>
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── LOGIN ──
  return (
    <div className="fixed inset-0 z-[997] flex flex-col overflow-hidden" style={bgStyle}>
      <div className="absolute top-[-60px] right-[-50px] w-56 h-56 rounded-full pointer-events-none" style={{ background: 'rgba(59,130,246,0.08)' }} />
      <div className="pt-12 px-5 pb-2 relative z-10">
        <button onClick={onBack} className="flex items-center gap-2 text-blue-300 text-sm font-medium">
          <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-xl">
            <i className="ri-arrow-left-line text-white text-sm"></i>
          </div>
          <span>Changer de profil</span>
        </button>
      </div>
      <div className="px-6 pt-4 pb-6 text-center relative z-10">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 shadow-xl" style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}>
          <i className="ri-steering-2-line text-white text-2xl"></i>
        </div>
        <h1 className="text-white font-extrabold text-xl mb-1">Connexion Conducteur</h1>
        <p className="text-blue-300 text-sm">Espace conducteur AutoLink</p>
      </div>
      <div className="flex-1 px-5 relative z-10">
        <div className="rounded-3xl p-5" style={{ background: 'rgba(255,255,255,0.07)', border: '1.5px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(12px)' }}>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-blue-300 uppercase tracking-wide mb-1.5">Email</label>
              <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 ${email ? 'border-2 border-blue-400/60 bg-blue-500/10' : 'border border-white/15 bg-white/5'}`}>
                <i className="ri-mail-line text-white/40 text-sm"></i>
                <input type="email" value={email} onChange={e => { setEmail(e.target.value); setLoginError(''); }} placeholder="votre@email.com" autoComplete="email" className="flex-1 text-sm text-white outline-none bg-transparent placeholder-white/25" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-blue-300 uppercase tracking-wide mb-1.5">Mot de passe</label>
              <div className={`flex items-center gap-3 rounded-2xl px-4 py-3 ${password ? 'border-2 border-blue-400/60 bg-blue-500/10' : 'border border-white/15 bg-white/5'}`}>
                <i className="ri-lock-line text-white/40 text-sm"></i>
                <input type={showPwd ? 'text' : 'password'} value={password} onChange={e => { setPassword(e.target.value); setLoginError(''); }} placeholder="••••••••••" autoComplete="current-password" className="flex-1 text-sm text-white outline-none bg-transparent placeholder-white/25" />
                <button type="button" onClick={() => setShowPwd(v => !v)} className="w-5 h-5 flex items-center justify-center">
                  <i className={`${showPwd ? 'ri-eye-off-line' : 'ri-eye-line'} text-white/30 text-base`}></i>
                </button>
              </div>
            </div>
            {loginError && (
              <div className="flex items-start gap-2 bg-red-500/15 border border-red-500/25 rounded-xl p-3">
                <i className="ri-error-warning-line text-red-400 text-sm flex-shrink-0 mt-0.5"></i>
                <p className="text-xs text-red-300 font-medium leading-snug">{loginError}</p>
              </div>
            )}
            <button type="submit" disabled={!email || !password || loginLoading} className="w-full py-3.5 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 mt-2 disabled:opacity-40" style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)' }}>
              <i className={`${loginLoading ? 'ri-loader-4-line animate-spin' : 'ri-login-box-line'} text-white text-sm`}></i>
              <span className="text-white">{loginLoading ? 'Connexion...' : 'Se connecter'}</span>
            </button>
          </form>
        </div>
        <button onClick={() => setStep('register')} className="w-full mt-4 text-center text-blue-300 text-xs font-medium py-2">
          Pas encore de compte ? <span className="text-white font-bold underline">Créer un compte</span>
        </button>
        <button onClick={() => setShowForgot(true)} className="w-full mt-1 text-center text-white/40 text-xs py-2">
          Mot de passe oublié ? <span className="text-blue-300 underline">Réinitialiser</span>
        </button>
      </div>
    </div>
  );
}
